import { Box, Checkbox, HStack, Tr, useToast } from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import { TbLayoutGrid, TbShieldLock } from 'react-icons/tb';
import SessionContext, { isInvoicingOrgSession } from '../../../../contexts/SessionContext';
import {
    useEditDefaultPermissions,
    type UserPermission,
} from '../../../../hooks/useEditDefaultPermissions';
import type { Permission, Role, WebsiteTab } from '../../../../hooks/useValidateSession';
import {
    EDITABLE_CELL_GEOMETRY,
    EDITABLE_CELL_HOVER,
    SettingsActionBar,
    SettingsTable,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
} from '../../../../components/settings/primitives';
import {
    PERMISSION_LABELS,
    ROLES,
    ROLE_LABELS,
    TAB_LABELS,
    buildMatrix,
    matrixEquals,
    toggleValue,
    visibleMatrixRows,
    type DefaultPermissionsMatrix,
} from './permissionCatalog';

export interface PermissionsSectionProps {
    /** Current defaults, from the users section of the settings response. */
    defaultPermissions: Record<Role, UserPermission>;
}

type MatrixView = 'tabs' | 'permissions';

/**
 * Default tabs and permissions per role, applied to newly invited users and to
 * anyone without per-user overrides.
 *
 * This writes to `PATCH /settings/permissions/edit`, its own endpoint, so it keeps its
 * own action bar rather than joining the shared settings draft.
 */
const PermissionsSection = ({ defaultPermissions }: PermissionsSectionProps) => {
    const session = useContext(SessionContext);
    const editDefaultPermissions = useEditDefaultPermissions();
    const toast = useToast();

    const serverMatrix = useMemo(() => buildMatrix(defaultPermissions), [defaultPermissions]);
    const [matrix, setMatrix] = useState<DefaultPermissionsMatrix>(serverMatrix);
    const [view, setView] = useState<MatrixView>('tabs');

    const invoicingEnabled = session?.invoicingEnabled === true;
    const inventoryEnabled = session?.inventoryEnabled === true;
    const invoicingOnly = isInvoicingOrgSession(session);

    const visibleRoles = useMemo(
        () =>
            ROLES.filter((role) => {
                // Invoicing orgs invite invoicing users only. Admin is a flag, not a column.
                if (invoicingOnly) return role === 'invoicing';
                if (role === 'invoicing') return invoicingEnabled;
                if (role === 'technician') return inventoryEnabled;
                return true;
            }),
        [invoicingEnabled, inventoryEnabled, invoicingOnly],
    );

    const { tabs: visibleTabs, permissions: visiblePermissions } = useMemo(
        () => visibleMatrixRows({ invoicingEnabled, inventoryEnabled, invoicingOnly }),
        [invoicingEnabled, inventoryEnabled, invoicingOnly],
    );

    const isDirty = !matrixEquals(matrix, serverMatrix);

    const toggleTab = (role: Role, tab: WebsiteTab) => {
        setMatrix((prev) => ({
            ...prev,
            [role]: { ...prev[role], tabs: toggleValue(prev[role].tabs, tab) },
        }));
    };

    const togglePermission = (role: Role, permission: Permission) => {
        setMatrix((prev) => ({
            ...prev,
            [role]: { ...prev[role], permissions: toggleValue(prev[role].permissions, permission) },
        }));
    };

    const handleSave = async () => {
        try {
            await editDefaultPermissions.mutateAsync({ defaultPermissions: matrix });
            toast({
                title: 'Default permissions saved',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch (error: any) {
            toast({
                title: 'Failed to save default permissions',
                description: error?.response?.data?.error || 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const isTabsView = view === 'tabs';
    const rows: Array<Permission | WebsiteTab> = isTabsView ? visibleTabs : visiblePermissions;
    const labels: Record<string, string> = isTabsView ? TAB_LABELS : PERMISSION_LABELS;

    // Fixed first column, then one equal share of the remainder per role, so the
    // checkbox columns line up regardless of how long each role label is.
    const NAME_COL_WIDTH = 220;
    const MIN_ROLE_COL_WIDTH = 116;
    const roleColWidth = `calc((100% - ${NAME_COL_WIDTH}px) / ${visibleRoles.length})`;
    const minTableWidth = `${NAME_COL_WIDTH + visibleRoles.length * MIN_ROLE_COL_WIDTH}px`;

    const isChecked = (role: Role, item: Permission | WebsiteTab) =>
        isTabsView
            ? matrix[role].tabs.includes(item as WebsiteTab)
            : matrix[role].permissions.includes(item as Permission);

    const onToggle = (role: Role, item: Permission | WebsiteTab) =>
        isTabsView ? toggleTab(role, item as WebsiteTab) : togglePermission(role, item as Permission);

    // Header check-all: rewrites only the rows visible in the current view, so
    // feature-gated (hidden) tabs/permissions in the matrix stay untouched.
    const setAllForRole = (role: Role, enabled: boolean) => {
        setMatrix((prev) => {
            const current = prev[role];
            if (isTabsView) {
                const kept = current.tabs.filter((tab) => !visibleTabs.includes(tab));
                return {
                    ...prev,
                    [role]: { ...current, tabs: enabled ? [...kept, ...visibleTabs] : kept },
                };
            }
            const kept = current.permissions.filter(
                (permission) => !visiblePermissions.includes(permission),
            );
            return {
                ...prev,
                [role]: {
                    ...current,
                    permissions: enabled ? [...kept, ...visiblePermissions] : kept,
                },
            };
        });
    };

    return (
        <>
            <SettingsTableTabs
                tabs={[
                    {
                        id: 'tabs',
                        label: 'Tabs',
                        icon: TbLayoutGrid as React.ElementType,
                        count: visibleTabs.length,
                    },
                    {
                        id: 'permissions',
                        label: 'Permissions',
                        icon: TbShieldLock as React.ElementType,
                        count: visiblePermissions.length,
                    },
                ]}
                value={view}
                onChange={(id) => setView(id as MatrixView)}
            />

            <SettingsTable
                fitWidth
                minTableWidth={minTableWidth}
                head={
                    <>
                        <SettingsTh
                            position="sticky"
                            left={0}
                            bg="gray.50"
                            zIndex={2}
                            width={`${NAME_COL_WIDTH}px`}
                        >
                            {isTabsView ? 'Tab' : 'Permission'}
                        </SettingsTh>
                        {visibleRoles.map((role) => {
                            const allChecked =
                                rows.length > 0 && rows.every((item) => isChecked(role, item));
                            const someChecked = rows.some((item) => isChecked(role, item));
                            return (
                                <SettingsTh
                                    key={role}
                                    textAlign="center"
                                    whiteSpace="nowrap"
                                    width={roleColWidth}
                                >
                                    <HStack spacing={1.5} justify="center">
                                        <Checkbox
                                            size="sm"
                                            colorScheme="partbay-blue"
                                            isChecked={allChecked}
                                            isIndeterminate={!allChecked && someChecked}
                                            onChange={(e) => setAllForRole(role, e.target.checked)}
                                            aria-label={`Toggle all ${isTabsView ? 'tabs' : 'permissions'} for ${ROLE_LABELS[role]}`}
                                        />
                                        <Box as="span">{ROLE_LABELS[role]}</Box>
                                    </HStack>
                                </SettingsTh>
                            );
                        })}
                    </>
                }
            >
                {rows.map((item) => (
                    <Tr key={item} _hover={{ bg: 'gray.50' }}>
                        <SettingsTd
                            position="sticky"
                            left={0}
                            bg="white"
                            zIndex={1}
                            fontWeight="medium"
                            color="gray.800"
                            whiteSpace="nowrap"
                        >
                            {labels[item] ?? item}
                        </SettingsTd>
                        {visibleRoles.map((role) => (
                            <SettingsTd key={role} textAlign="center">
                                {/* The Checkbox root is a label, so stretching it
                                    across the cell makes the whole cell the
                                    click target and lets it carry the same
                                    input-outline hover as other editable cells. */}
                                <Checkbox
                                    colorScheme="partbay-blue"
                                    isChecked={isChecked(role, item)}
                                    onChange={() => onToggle(role, item)}
                                    aria-label={`${ROLE_LABELS[role]} — ${labels[item] ?? item}`}
                                    display="flex"
                                    justifyContent="center"
                                    {...EDITABLE_CELL_GEOMETRY}
                                    {...EDITABLE_CELL_HOVER}
                                />
                            </SettingsTd>
                        ))}
                    </Tr>
                ))}
            </SettingsTable>

            <SettingsActionBar
                isDirty={isDirty}
                isSaving={editDefaultPermissions.isPending}
                onSave={handleSave}
                onDiscard={() => setMatrix(serverMatrix)}
            />
        </>
    );
};

export default PermissionsSection;
